import React, { useState, useEffect, useContext } from 'react';
import Grid from '@mui/material/Grid';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import "./pendingResumeValidationsForm.css";
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { DateTime } from "luxon";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import PhoneInput, { isPossiblePhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormHelperText from '@mui/material/FormHelperText';
import getAllPrimarySkillData from "../../apis/getAllPrimarySkillData";
import { NotificationManager } from "react-notifications";
import { useNavigate, useLocation } from "react-router-dom";
import updatePendingResume from "../../apis/updateCandidateResumeById/updateCandidateResumeById";
import { UserTypeContext } from '../../AppRoute';
import fetchCountryStateCity from '../../apis/fetchCountryStateCity';

const styles = {
    root: { flexGrow: 1 }
};

const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option) => option.name,
});

const PendingResumeValidationsForm = ({
    candidateResume,
    idContextTest,
    showPendingResumeForm,
    userType,
    dataResult,
    setDataResult,
    userRecruitmentpartnerId,
    candidateResumeId,
    setShowPendingResumeForm
}) => {
    const history = useNavigate();
    const location = useLocation();
    const { userRecords } = useContext(UserTypeContext);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [atvBtn, setAtvBtn] = useState(false);

    // Initialize states with candidateResume data
    const [contactInfo, setContactInfo] = useState({
        emailID: "",
        emailError: "",
        phoneNumber: "",
        phoneCode: "",
        phoneError: true,
        phoneNumberInt: "",
        isPhoneNumberBlank: false,
        isEmailIdDisabled: false,
        phoneCodeError: false, // Tracks empty countryCallingCode
    });

    const [personalInfo, setPersonalInfo] = useState({
        name: "",
        uploadedBy: "",
        initialUploadDate: "",
        lastUploadDate: "",
        noticePeriod: "",
        yearsOfExperience: "",
        currentCompensation: "",
        expectedCompensation: "",
    });

    const [locationList, setLocationList] = useState({
        countryList: [],
        stateList: [],
        cityList: [],
        currencyList: [],
        isCountryFetching: false,
        isStateFetching: false,
        isCityFetching: false,
        isCurrencyFetching: false,
    });

    const [locationInfo, setLocationInfo] = useState({
        cityName: candidateResume?.city || "",
        stateName: candidateResume?.state || "",
        countryName: candidateResume?.country || "",
        cityList: [],
        stateList: [],
        selectedCountry: null,
        selectedState: null,
        selectedCity: null,
        defaultCountryCode: "",
        currencyValue: "",
        countryCode: candidateResume?.countryCode || "",
        countryCallingCode: candidateResume?.countryCallingCode || ""
    });

    const [skillsInfo, setSkillsInfo] = useState({
        primarySkill: [],
        primarySkillOptions: [],
        selectedPrimarySkill: "",
        primarySkillTextFieldValue: "",
        additionalSkills: [],
        additionalSkillOptions: [],
        currentAdditionalSkillValue: "",
    });

    // Fetch country, state, or city data
    const fetchLocationInformation = async (field, source) => {
        let response = null;
        try {
            if (field === "country") {
                setLocationList((prev) => ({ ...prev, isCountryFetching: true }));
                response = await fetchCountryStateCity({ actionName: "country" });
                if (response.status === 200) {
                    const countryListData = response.data.data;
                    const uniqueCurrencies = [...new Set(countryListData.map(item => item.currency).filter(Boolean))];
                    setLocationList({
                        ...locationList,
                        isCountryFetching: false,
                        countryList: countryListData,
                        currencyList: uniqueCurrencies
                    });
                }
            } else if (field === "state") {
                setLocationList((prev) => ({ ...prev, isStateFetching: true }));
                response = await fetchCountryStateCity({
                    actionName: "state",
                    countryName: locationInfo.selectedCountry?.name,
                });
                if (response.status === 200) {
                    setLocationList({
                        ...locationList,
                        isStateFetching: false,
                        stateList: response.data.data
                    });
                }
            } else if (field === "city") {
                setLocationList((prev) => ({ ...prev, isCityFetching: true }));
                response = await fetchCountryStateCity({
                    actionName: "city",
                    stateName: locationInfo.stateName,
                });
                if (response.status === 200) {
                    setLocationList({
                        ...locationList,
                        isCityFetching: false,
                        cityList: response.data.data
                    });
                }
            }
            if (source === "initialLoad") return response.data.data;
        } catch (error) {
            NotificationManager.error(
                `Unable to load ${field} list. Please try again later.`,
                "Error",
                10000
            );
            setLocationList((prev) => ({
                ...prev,
                [`is${field.charAt(0).toUpperCase() + field.slice(1)}Fetching`]: false,
                [`${field}List`]: []
            }));
        }
    };

    // Initialize form data on candidateResume change
    useEffect(() => {
        const fetchData = async () => {
            const contactInfoToBeSet = {};
            const personalInfoToBeSet = {};
            const skillsInfoToBeSet = {};
            const locationInfoToBeSet = {};

            // Handle phone number and calling code
            const countryCallingCode = candidateResume?.countryCallingCode || "";
            if (candidateResume?.phoneNumber) {
                let tempPhoneNumber = candidateResume.phoneNumber.replace(/[()-]/g, '');
                let originalPhoneNumber = tempPhoneNumber.startsWith("+") ? tempPhoneNumber : `${countryCallingCode}${tempPhoneNumber}`;
                let parsedPhone = parsePhoneNumber(originalPhoneNumber);
                contactInfoToBeSet.phoneNumber = parsedPhone?.nationalNumber || originalPhoneNumber;
                contactInfoToBeSet.phoneCode = parsedPhone?.countryCallingCode ? `+${parsedPhone.countryCallingCode}` : countryCallingCode;
                contactInfoToBeSet.phoneNumberInt = originalPhoneNumber;
                contactInfoToBeSet.isPhoneNumberBlank = false;
                contactInfoToBeSet.phoneError = !isPossiblePhoneNumber(originalPhoneNumber);
                contactInfoToBeSet.phoneCodeError = !countryCallingCode; // Set error if countryCallingCode is empty
            } else {
                contactInfoToBeSet.phoneNumberInt = "";
                contactInfoToBeSet.phoneCode = countryCallingCode;
                contactInfoToBeSet.phoneNumber = "";
                contactInfoToBeSet.isPhoneNumberBlank = true;
                contactInfoToBeSet.phoneError = false;
                contactInfoToBeSet.phoneCodeError = !countryCallingCode;
            }

            locationInfoToBeSet.countryCallingCode = countryCallingCode;

            // Handle country and currency
            if (candidateResume?.country) {
                const parsedCountry = candidateResume?.country || null;
                locationInfoToBeSet.selectedCountry = parsedCountry ? { name: parsedCountry } : null;
                if ((!candidateResume?.countryCode || !candidateResume?.currency) && parsedCountry) {
                    let countryListData = locationList.countryList.length > 0 ? locationList.countryList : await fetchLocationInformation("country", "initialLoad");
                    const countryData = countryListData?.find(item => item.name.trim() === parsedCountry.trim());
                    if (countryData) {
                        locationInfoToBeSet.currencyValue = candidateResume.currency || countryData.currency || "";
                        locationInfoToBeSet.defaultCountryCode = candidateResume.isoCode || countryData.isoCode || "";
                        locationInfoToBeSet.countryCode = candidateResume.isoCode || countryData.isoCode || "";
                    }
                    else {
                        locationInfoToBeSet.currencyValue = candidateResume.currency || "";
                        locationInfoToBeSet.defaultCountryCode = candidateResume.isoCode || "";
                        locationInfoToBeSet.countryCode = candidateResume.isoCode || "";
                    }
                }
                else {
                    locationInfoToBeSet.countryCode = candidateResume.countryCode || "";
                    locationInfoToBeSet.defaultCountryCode = candidateResume.countryCode || "";
                    locationInfoToBeSet.currencyValue = candidateResume.currency || "";
                }
            }

            // Other fields
            contactInfoToBeSet.emailID = candidateResume?.emailID || "";
            contactInfoToBeSet.isEmailIdDisabled = candidateResume?.isEmailIdDisabled || false;
            personalInfoToBeSet.name = candidateResume?.name || "";
            personalInfoToBeSet.uploadedBy = candidateResume?.recruiterName || "";
            personalInfoToBeSet.initialUploadDate = candidateResume?.initialUploadDate || "";
            personalInfoToBeSet.lastUploadDate = candidateResume?.lastUploadDate || "";
            personalInfoToBeSet.yearsOfExperience = candidateResume?.yr_of_exp || "";
            personalInfoToBeSet.currentCompensation = candidateResume?.currentSalary || "";
            personalInfoToBeSet.expectedCompensation = candidateResume?.expectedSalary || "";
            personalInfoToBeSet.noticePeriod = candidateResume?.noticePeriod || "";
            skillsInfoToBeSet.selectedPrimarySkill = candidateResume?.primarySkill || "";
            skillsInfoToBeSet.additionalSkills = candidateResume?.skills || [];
            locationInfoToBeSet.selectedState = candidateResume?.state ? { name: candidateResume.state } : null;
            locationInfoToBeSet.selectedCity = candidateResume?.city ? { name: candidateResume.city } : null;

            setContactInfo(prev => ({ ...prev, ...contactInfoToBeSet }));
            setPersonalInfo(prev => ({ ...prev, ...personalInfoToBeSet }));
            setSkillsInfo(prev => ({ ...prev, ...skillsInfoToBeSet }));
            setLocationInfo(prev => ({ ...prev, ...locationInfoToBeSet }));
        };

        fetchData();
        // eslint-disable-next-line
    }, [candidateResume, userType]);

    const handleEmailChange = (e) => {
        const email = e.target.value;
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        setContactInfo(prev => ({
            ...prev,
            emailID: email,
            emailError: emailRegex.test(email) ? "" : "Invalid email address"
        }));
    };

    const handlePhoneNumberChange = (val) => {
        const contactInfoToBeSet = {};
        if (val) {
            contactInfoToBeSet.isPhoneNumberBlank = false;
            const parsedPhone = parsePhoneNumber(val);
            if (parsedPhone?.countryCallingCode) {
                const countryCallingCode_Input = `+${parsedPhone.countryCallingCode}`;
                contactInfoToBeSet.phoneCode = countryCallingCode_Input;
                contactInfoToBeSet.phoneNumber = parsedPhone.nationalNumber || "";
                contactInfoToBeSet.phoneNumberInt = val;
                contactInfoToBeSet.phoneCodeError = false; // Clear error on valid input
            } else {
                contactInfoToBeSet.phoneNumberInt = val;
                contactInfoToBeSet.phoneNumber = val;
                contactInfoToBeSet.phoneCode = "";
                contactInfoToBeSet.phoneCodeError = true;
            }
            contactInfoToBeSet.phoneError = !isPossiblePhoneNumber(val);
        } else {
            contactInfoToBeSet.phoneError = false;
            contactInfoToBeSet.phoneNumber = "";
            contactInfoToBeSet.phoneCode = "";
            contactInfoToBeSet.isPhoneNumberBlank = true;
            contactInfoToBeSet.phoneCodeError = true;
        }
        setContactInfo(prev => ({ ...prev, ...contactInfoToBeSet }));
    };

    const handleCountryChange = (event, value) => {
        const dataTobeUpdated = value ? {
            countryName: value.name || "",
            selectedCountry: value || null,
            currencyValue: value.currency || "",
            defaultCountryCode: value.isoCode || "",
            countryCode: value.isoCode || "",
            countryCallingCode: value.callingCode || "" // Set for payload, doesn't affect phone
        } : {
            countryName: "",
            selectedCountry: null,
            currencyValue: "",
            defaultCountryCode: "",
            countryCode: "",
            countryCallingCode: ""
        };

        setLocationInfo(prev => ({
            ...prev,
            ...dataTobeUpdated,
            stateList: [],
            cityList: [],
            selectedState: null,
            selectedCity: null,
            stateName: "",
            cityName: "",
        }));
    };

    const handleStateChange = (e, value) => {
        setLocationInfo(prev => ({
            ...prev,
            stateName: value?.name || "",
            selectedState: value || null,
            cityList: [],
            selectedCity: null,
            cityName: ""
        }));
        setLocationList(prev => ({ ...prev, cityList: [] }));
    };

    const handleCityChange = (e, value) => {
        setLocationInfo(prev => ({
            ...prev,
            cityName: value?.name || "",
            selectedCity: value || null
        }));
    };

    const currencyChangeHandler = (e, value) => {
        setLocationInfo(prev => ({ ...prev, currencyValue: value }));
    };

    const handlePrimarySkillsChange = (event, newValue) => {
        setSkillsInfo(prev => ({
            ...prev,
            selectedPrimarySkill: newValue || "",
            primarySkill: skillsInfo.primarySkillOptions
        }));
    };

    const handlePrimarySkillTextFieldChange = (e) => {
        const typedSkill = e.target.value;
        const skillsInfoToBeSet = { primarySkillTextFieldValue: typedSkill };
        if (typedSkill) {
            const arr = [];
            let isSkillExists = false;
            skillsInfo.primarySkillOptions.forEach(element => {
                if (element.toLowerCase().startsWith(typedSkill.toLowerCase())) {
                    arr.push(element);
                }
                if (element.toLowerCase() === typedSkill.toLowerCase()) {
                    isSkillExists = true;
                }
            });
            if (!isSkillExists) arr.unshift(typedSkill);
            skillsInfoToBeSet.primarySkill = arr;
        } else {
            skillsInfoToBeSet.primarySkill = skillsInfo.primarySkillOptions;
        }
        setSkillsInfo(prev => ({ ...prev, ...skillsInfoToBeSet }));
    };

    const additionalSkillTextFieldHandler = (e) => {
        setSkillsInfo(prev => ({
            ...prev,
            additionalSkillOptions: [e.target.value],
            currentAdditionalSkillValue: e.target.value
        }));
    };

    const additionalSkillChangeHandler = (e, value) => {
        const lastValue = value[value.length - 1];
        const index = value.findIndex(option => option.toLowerCase() === lastValue?.toLowerCase());
        if (index === -1 || index === value.length - 1) {
            setSkillsInfo(prev => ({
                ...prev,
                additionalSkills: value,
                additionalSkillOptions: []
            }));
        }
    };

    const openResume = () => {
        if (candidateResume.resumeUrl) window.open(candidateResume.resumeUrl, '_blank');
    };

    const handleYearsOfExperienceChange = (e) => {
        setPersonalInfo(prev => ({ ...prev, yearsOfExperience: e.target.value }));
    };

    const handleCurrentCompensationChange = (e) => {
        setPersonalInfo(prev => ({ ...prev, currentCompensation: e.target.value }));
    };

    const handleExpectedCompensationChange = (e) => {
        setPersonalInfo(prev => ({ ...prev, expectedCompensation: e.target.value }));
    };

    const handleNoticePeriod = (e) => {
        setPersonalInfo(prev => ({ ...prev, noticePeriod: e.target.value }));
    };

    const keypressHandler = (e) => {
        if (["-", "+", "e"].includes(e.key)) e.preventDefault();
    };

    const goToMyTalentPage = () => {
        if (!showPendingResumeForm) {
            history(userType === 'Admin' ? "/recruiter-talent-community" : "/recruiter-dashboard", {
                state: userType === 'Admin' ? { id: location.state?.id } : undefined
            });
        } else {
            setShowPendingResumeForm(false);
        }
    };

    const submitButtonHandler = async () => {
        setAtvBtn(true);
        const candidateParsing = candidateResume?.isParsing;
        const payload = {
            emailID: contactInfo.emailID,
            phoneNumber: contactInfo.phoneError ? "invalid" : contactInfo.phoneNumberInt,
            name: personalInfo.name,
            primarySkill: skillsInfo.selectedPrimarySkill,
            city: locationInfo.cityName,
            state: locationInfo.stateName,
            country: locationInfo.countryName,
            countryCode: candidateResume?.countryCode || locationInfo.countryCode,
            countryCallingCode: locationInfo.countryCallingCode || contactInfo.phoneCode,
            currency: locationInfo.currencyValue,
            skills: skillsInfo.additionalSkills,
            yr_of_exp: personalInfo.yearsOfExperience,
            currentSalary: parseInt(personalInfo.currentCompensation) || 0,
            expectedSalary: parseInt(personalInfo.expectedCompensation) || 0,
            noticePeriod: parseInt(personalInfo.noticePeriod) || 0,
            lastUpdatedBy: idContextTest.idContextTest,
            recruitmentOrgId: userRecruitmentpartnerId,
            isParsing: candidateParsing
        };

        if (!payload.emailID) {
            setAtvBtn(false);
            NotificationManager.error("Please enter an email address", "Error", 10000);
        } else if (!payload.primarySkill) {
            setAtvBtn(false);
            NotificationManager.error("Please enter a Primary Skill", "Error", 10000);
        } else if (!payload.country) {
            setAtvBtn(false);
            NotificationManager.error("Please select a country", "Error", 10000);
        } else if (payload.emailID && contactInfo.emailError) {
            setAtvBtn(false);
            NotificationManager.error("Please enter a valid email address", "Error", 10000);
        } else if (payload.phoneNumber === "invalid") {
            setAtvBtn(false);
            NotificationManager.error("Please enter a valid phone number", "Error", 10000);
        } else if (!payload.phoneNumber && candidateParsing) {
            setAtvBtn(false);
            NotificationManager.error("Please enter a phone number", "Error", 10000);
        } else if (!payload.countryCallingCode) {
            setAtvBtn(false);
            NotificationManager.error("Please re-enter phone number with a valid country code", "Error", 10000);
        } else {
            try {

                payload.recruitmentOrgId = userRecruitmentpartnerId;
                payload.isParsing = candidateParsing;
                const response = await updatePendingResume(candidateResumeId, payload);
                if (response.status === 202) {
                    setAtvBtn(false);
                    NotificationManager.error(`${response.data.message}`, "error", 10000);
                }
                else {
                    let candidatesData = [...dataResult];
                    let index = candidatesData.findIndex((item) => item.candidateResumeId === candidateResumeId)
                    if (index !== -1) {
                        if (payload.emailID && payload.primarySkill && payload.phoneNumber && payload.country && payload.countryCode) {
                            candidatesData[index].isProfileComplete = true
                        }

                        candidatesData[index].city = payload.city
                        candidatesData[index].country = payload.country
                        candidatesData[index].countryCallingCode = payload.countryCallingCode
                        candidatesData[index].currentSalary = payload.currentSalary
                        candidatesData[index].emailID = payload.emailID
                        candidatesData[index].expectedSalary = payload.expectedSalary
                        candidatesData[index].name = payload.name
                        candidatesData[index].phoneNumber = payload.phoneNumber
                        candidatesData[index].primarySkill = payload.primarySkill
                        candidatesData[index].skills = payload.skills
                        candidatesData[index].state = payload.state
                        candidatesData[index].yr_of_exp = payload.yr_of_exp
                        candidatesData[index].noticePeriod = payload.noticePeriod
                        candidatesData[index].currency = payload.currency
                        candidatesData[index].countryCode = payload.countryCode
                        setDataResult(candidatesData)
                        goToMyTalentPage();
                        if (true) {
                            setAtvBtn(false);
                            NotificationManager.success("Resume is successfully updated", "success", 10000);
                        }
                    }
                }
            } catch (error) {
                if (error.response.status === 400) {
                    setAtvBtn(false);
                    const errorMessage = error.response.data.errMessage;
                    NotificationManager.error(errorMessage, "Error", 2500);
                }
                else {
                    NotificationManager.error("There is an error updating", "error", 10000);
                    setAtvBtn(false);
                }
                console.error("Error updating resume:", error);
            }
            setAtvBtn(false);
        }
    };

    // Fetch primary skills on mount
    useEffect(() => {
        const fetchPrimarySkills = async () => {
            try {
                const payload = {
                    userRecruitmentpartnerId: userRecords?.recruitmentpartnerId || [],
                    userId: userRecords?.id || []
                };
                const response = await getAllPrimarySkillData(payload);
                const skills = response.data.data.map(skill => skill.name);
                setSkillsInfo(prev => ({ ...prev, primarySkillOptions: skills, primarySkill: skills }));
            } catch (error) {
                console.error('Error fetching primary skills:', error);
            }
        };
        fetchPrimarySkills();
        // eslint-disable-next-line
    }, [userType]);

    return (
        <div sx={styles.root} className="pendingResumeForm__container">
            <Grid container spacing={2} wrap="wrap" className="pendingResumeForm__grid">
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Email Address*</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Email Address"
                            className={contactInfo.emailID ? "admin_textbox" : "admin_textbox_error"}
                            value={contactInfo.emailID}
                            onChange={handleEmailChange}
                            disabled={contactInfo.isEmailIdDisabled || candidateResume?.uploadedBy ? false : true}
                        />
                        <FormHelperText error>{contactInfo.emailError}</FormHelperText>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }} className='phoneNumber_pendingResume'>
                    <div className="inputLabel_right">Phone Number{candidateResume.isParsing ? "*" : ""}</div>
                    <FormControl fullWidth variant="outlined">
                        <PhoneInput
                            international
                            defaultCountry={contactInfo.phoneCode ? undefined : locationInfo.defaultCountryCode || contactInfo.phoneCode}
                            className={
                                (candidateResume.isParsing && contactInfo.isPhoneNumberBlank)
                                    ? "admin_textbox_error"
                                    : "textbox_phone_input_pendingResume"
                            }
                            placeholder="Enter Phone Number"
                            value={
                                contactInfo?.phoneNumberInt?.startsWith('+')
                                    ? contactInfo.phoneNumberInt
                                    : `${contactInfo?.phoneCode || ''} ${contactInfo?.phoneNumber || ''}`
                            }
                            onChange={handlePhoneNumberChange}
                            onKeyPress={keypressHandler}
                            disabled={candidateResume?.uploadedBy ? false : true}
                        />
                        {contactInfo.phoneError && (
                            <FormHelperText error>Please enter a valid phone number</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Name</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Name"
                            className="admin_textbox"
                            value={personalInfo.name}
                            onChange={e => setPersonalInfo(prev => ({ ...prev, name: e.target.value }))}
                        />
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Primary Skill*</div>
                    <div className="autocomplete-container">
                        <Autocomplete
                            id="primary-skill"
                            getOptionLabel={option => option}
                            options={skillsInfo.primarySkill}
                            value={skillsInfo.selectedPrimarySkill}
                            freeSolo
                            className={skillsInfo.selectedPrimarySkill ? "admin_textbox" : "admin_textbox_primarySkill_error"}
                            onChange={handlePrimarySkillsChange}
                            filterSelectedOptions
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Primary Skill"
                                    variant="outlined"
                                    value={skillsInfo.primarySkillTextFieldValue}
                                    onChange={handlePrimarySkillTextFieldChange}
                                    fullWidth
                                />
                            )}
                        />
                    </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Additional Skill</div>
                    <div className="autocomplete-container">
                        <Autocomplete
                            id="additional-skills"
                            multiple
                            freeSolo
                            filterSelectedOptions
                            options={skillsInfo.additionalSkillOptions}
                            value={skillsInfo.additionalSkills}
                            getOptionLabel={option => option}
                            onChange={additionalSkillChangeHandler}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    value={skillsInfo.currentAdditionalSkillValue}
                                    onChange={additionalSkillTextFieldHandler}
                                    placeholder="Enter Skills"
                                />
                            )}
                            renderTags={(value, getTagProps) => {
                                if (value.length > 3) {
                                    const firstThree = value.slice(0, 3);
                                    const remainingCount = value.length - 3;
                                    return (
                                        <>
                                            {firstThree.map((option, index) => (
                                                <Chip
                                                    style={option.length > 20 ? { width: "185px" } : {}}
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                />
                                            ))}
                                            <Chip
                                                label={`+${remainingCount} more`}
                                                onClick={() => setIsDialogOpen(true)}
                                            />
                                        </>
                                    );
                                }
                                return value.map((option, index) => (
                                    <Chip label={option} {...getTagProps({ index })} />
                                ));
                            }}
                        />
                    </div>
                    <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullWidth maxWidth="md">
                        <DialogTitle>Additional Skills</DialogTitle>
                        <DialogContent>
                            <Autocomplete
                                id="additional-skills-dialog"
                                multiple
                                freeSolo
                                filterSelectedOptions
                                disableClearable={false}
                                options={skillsInfo.additionalSkillOptions}
                                value={skillsInfo.additionalSkills}
                                getOptionLabel={option => option}
                                onChange={additionalSkillChangeHandler}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        value={skillsInfo.currentAdditionalSkillValue}
                                        onChange={additionalSkillTextFieldHandler}
                                        placeholder="Enter Skills"
                                    />
                                )}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsDialogOpen(false)} color="primary">Close</Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Country*</div>
                    <Autocomplete
                        id="country"
                        className={locationInfo.selectedCountry ? "mt_14" : "admin_textbox_error"}
                        options={locationList.countryList}
                        autoHighlight
                        filterSelectedOptions
                        getOptionLabel={option => option.name || ""}
                        onFocus={() => locationList.countryList.length > 0 ? null : fetchLocationInformation("country", "dropdown")}
                        loading={locationList.isCountryFetching}
                        value={locationInfo.selectedCountry}
                        onChange={handleCountryChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                required
                                placeholder="Please Select Country"
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {locationList.isCountryFetching && <CircularProgress color="inherit" size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">State</div>
                    <Autocomplete
                        id="state"
                        className="mt_14"
                        options={locationList.stateList}
                        autoHighlight
                        filterOptions={filterOptions}
                        getOptionLabel={option => option?.name || ""}
                        loading={locationList.isStateFetching}
                        onFocus={() => locationList.stateList.length > 0 || !locationInfo?.selectedCountry?.name ? null : fetchLocationInformation("state", "dropdown")}
                        value={locationInfo.selectedState}
                        onChange={handleStateChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Please Select State"
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {locationList.isStateFetching && <CircularProgress color="inherit" size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">City</div>
                    <Autocomplete
                        id="city"
                        className="mt_14"
                        options={locationList.cityList}
                        autoHighlight
                        filterOptions={filterOptions}
                        onFocus={() => locationList.cityList.length > 0 || !locationInfo?.selectedState?.name ? null : fetchLocationInformation("city", "dropdown")}
                        loading={locationList.isCityFetching}
                        getOptionLabel={option => option.name || ""}
                        value={locationInfo.selectedCity}
                        onChange={handleCityChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Please Select City"
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {locationList.isCityFetching && <CircularProgress color="inherit" size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Years of Experience</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Years of Experience"
                            className="admin_textbox"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={personalInfo.yearsOfExperience}
                            onChange={handleYearsOfExperienceChange}
                            onKeyPress={keypressHandler}
                        />
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 1.1 }}>
                    <div className="inputLabel_right">Currency</div>
                    <Autocomplete
                        id="currency-current"
                        disableClearable
                        className="admin_textbox_currency"
                        options={locationList.currencyList}
                        onFocus={() => locationList.currencyList.length > 0 ? null : fetchLocationInformation("country", "dropdown")}
                        loading={locationList.isCountryFetching}
                        getOptionLabel={option => option || ""}
                        value={locationInfo.currencyValue || ""}
                        onChange={currencyChangeHandler}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Currency"
                                variant="outlined"
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {locationList.isCountryFetching && <CircularProgress color="inherit" size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 1.9 }}>
                    <div className="inputLabel_right">Current Compensation</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Current Compensation"
                            className="admin_textbox"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={personalInfo.currentCompensation}
                            onChange={handleCurrentCompensationChange}
                            onKeyPress={keypressHandler}
                        />
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 1.1 }}>
                    <div className="inputLabel_right">Currency</div>
                    <Autocomplete
                        id="currency-expected"
                        className="admin_textbox_currency"
                        placeholder="Currency"
                        options={locationList.currencyList}
                        disableClearable
                        onFocus={() => locationList.currencyList.length > 0 ? null : fetchLocationInformation("country", "dropdown")}
                        loading={locationList.isCountryFetching}
                        getOptionLabel={option => option || ""}
                        value={locationInfo.currencyValue || ""}
                        onChange={currencyChangeHandler}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Currency"
                                variant="outlined"
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {locationList.isCountryFetching && <CircularProgress color="inherit" size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 1.9 }}>
                    <div className="inputLabel_right">Expected Compensation</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Expected Compensation"
                            className="admin_textbox"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={personalInfo.expectedCompensation}
                            onChange={handleExpectedCompensationChange}
                            onKeyPress={keypressHandler}
                        />
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Notice Period (Days)</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Notice Period"
                            className="admin_textbox"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={personalInfo.noticePeriod}
                            onChange={handleNoticePeriod}
                            onKeyPress={keypressHandler}
                        />
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Uploaded By*</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Uploaded By"
                            className="uploadedBy_textbox admin_textbox"
                            value={personalInfo.uploadedBy}
                            disabled
                        />
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Initial Upload Date*</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Initial Upload Date"
                            className="admin_textbox"
                            value={
                                personalInfo.initialUploadDate
                                    ? DateTime.fromISO(personalInfo.initialUploadDate).toFormat("MM-dd-yyyy")
                                    : DateTime.fromISO(candidateResume?.createdAt).toFormat("MM-dd-yyyy")
                            }
                            disabled
                        />
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                    <div className="inputLabel_right">Last Upload Date*</div>
                    <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                            placeholder="Last Upload Date"
                            className="admin_textbox"
                            value={
                                personalInfo.lastUploadDate
                                    ? DateTime.fromISO(personalInfo.lastUploadDate).toFormat("MM-dd-yyyy")
                                    : DateTime.fromISO(candidateResume?.updatedAt).toFormat("MM-dd-yyyy")
                            }
                            disabled
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <div className="pending-validations-actions">
                {showPendingResumeForm && (
                    <>
                        <Button
                            variant="contained"
                            className={candidateResume.resumeUrl ? "pending-validations-actions__viewResume" : "pending-validations-actions__viewResume_blank"}
                            onClick={openResume}
                            disabled={!candidateResume.resumeUrl}
                        >
                            View Resume
                        </Button>
                        <button
                            className={
                                userType === "Admin" || atvBtn
                                    ? 'pending-validations-actions__submit_disable'
                                    : 'pending-validations-actions__submit'
                            }
                            onClick={submitButtonHandler}
                            disabled={atvBtn || userType === "Admin"}
                        >
                            <span>Submit</span>
                            {atvBtn && <CircularProgress className="circularProgress_updateButton" />}
                        </button>
                        <Button
                            variant="outlined"
                            className="pending-validations-actions__cancel"
                            onClick={goToMyTalentPage}
                        >
                            Cancel
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default PendingResumeValidationsForm;
